/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Pagination, Popover, PopoverContent, PopoverTrigger, Spinner, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow, Tooltip, User } from '@nextui-org/react';
import { useSelector } from 'react-redux';
import { IconEdit, IconMapPin, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';
import toast from 'react-hot-toast';
import moment from 'moment';
import { Constants, classNames, fromPhotos } from '../../../utils';
import { IconParent, IconTeacher } from '../../../assets/icons';
import { UsersService } from '../../../services';
import ModalCreateTeacher from './create-teacher';
import ModalCreateParent from './create-parent';
import ModalCreateParentInBulk from './create-parent-in-bulk';

const MODAL_ACTION = {
  NONE: 0,
  CREATE: 1,
  VIEW: 2,
  EDIT: 3,
  DELETE: 4,
  CREATE_IN_BULK: 5,
}

const getStatusColor = (status) => {
  switch (status) {
    case Constants.USER.STATUS.ACTIVE:
      return 'text-green';
    case Constants.USER.STATUS.INACTIVE:
      return 'text-danger';
    case Constants.USER.STATUS.PENDING:
      return 'text-warning';
    default:
      return 'text-black';
  }
}

const Users = () => {
  const [selectedItem, setSelectedItem] = useState();
  const [modalAction, setModalAction] = useState(MODAL_ACTION.NONE);
  const { canResetFilter, data, filters, filterBy, goToPage, isLoading, pagination, reload, deleteItem } = useFetchTable();

  const onSelectItem = (item, action) => {
    setSelectedItem(item);
    setModalAction(action);
  }

  const closeModal = (reloading = false) => {
    setSelectedItem(null);
    setModalAction(MODAL_ACTION.NONE);
    if (reloading) reload(true);
  }

  return (
    <div>
      {isLoading && (
        <div className="w-screen h-screen fixed inset-0 z-[70] flex justify-center items-center bg-white/30">
          <Spinner />
        </div>
      )}

      <Modal
        size="4xl"
        isOpen={[MODAL_ACTION.CREATE, MODAL_ACTION.EDIT].includes(modalAction)}
        onClose={() => closeModal()}
        backdrop="blur"
        scrollBehavior="outside"
      >
        <ModalContent>
          {(onClose) => {
            const isTeacher = filters.level_id === Constants.USER.LEVELS.PROFESSOR;
            const isParent = filters.level_id === Constants.USER.LEVELS.PARENT;

            if (modalAction === MODAL_ACTION.CREATE && isTeacher) return (
              <ModalCreateTeacher
                onClose={onClose}
                onSuccess={() => closeModal(true)}
              />
            );
            if (modalAction === MODAL_ACTION.EDIT && isTeacher) return (
              <ModalCreateTeacher
                user={selectedItem}
                onClose={onClose}
                onSuccess={() => closeModal(true)}
              />
            );
            if (modalAction === MODAL_ACTION.CREATE && isParent) return (
              <ModalCreateParent
                onClose={onClose}
                onSuccess={() => closeModal(true)}
              />
            );
            if (modalAction === MODAL_ACTION.EDIT && isParent) return (
              <ModalCreateParent
                user={selectedItem}
                onClose={onClose}
                onSuccess={() => closeModal(true)}
              />
            );
          }}
        </ModalContent>
      </Modal>

      <Modal
        size="md"
        isOpen={[MODAL_ACTION.DELETE, MODAL_ACTION.VIEW].includes(modalAction)}
        onClose={() => closeModal()}
        backdrop="blur"
        scrollBehavior="outside"
      >
        <ModalContent>
          {(onClose) => {
            const isTeacher = filters.level_id === Constants.USER.LEVELS.PROFESSOR;
            // const isParent = filters.level_id === Constants.USER.LEVELS.PARENT;

            if (modalAction === MODAL_ACTION.VIEW && isTeacher) return (
              <ModalViewSubjects item={selectedItem} />
            );
            if (modalAction === MODAL_ACTION.DELETE) return (
              <ModalDelete
                user={selectedItem}
                onClose={onClose}
                onDelete={() => {
                  deleteItem(selectedItem?.id);
                  closeModal(true);
                }}
              />
            );
          }}
        </ModalContent>
      </Modal>

      <Modal
        size="lg"
        isOpen={[MODAL_ACTION.CREATE_IN_BULK].includes(modalAction)}
        onClose={() => closeModal()}
        backdrop="blur"
        scrollBehavior="outside"
      >
        <ModalContent>
          {(onClose) => {
            const isParent = filters.level_id === Constants.USER.LEVELS.PARENT;

            if (modalAction === MODAL_ACTION.CREATE_IN_BULK && isParent) return (
              <ModalCreateParentInBulk
                onClose={onClose}
                onSuccess={() => closeModal(true)}
              />
            );
          }}
        </ModalContent>
      </Modal>

      <Filters
        canResetFilter={canResetFilter}
        filterBy={filterBy}
        resetFilter={() => reload()}
      />

      {filters.level_id === Constants.USER.LEVELS.PROFESSOR ? (
        <Table
          aria-label="Profesores Registrados"
          color="primary"
          topContentPlacement="inside"
          topContent={
            <div className="flex flex-row justify-between items-center gap-4">
              <h3 className="text-xl font-medium">Profesores Registrados</h3>
              <Button
                className="pr-2 bg-green font-medium text-white"
                endContent={<IconPlus color="white" />}
                onClick={() => onSelectItem(null, MODAL_ACTION.CREATE)}
              >
                Nuevo
              </Button>
            </div>
          }
        >
          <TableHeader>
            <TableColumn>Fecha de registro</TableColumn>
            <TableColumn>Nombre</TableColumn>
            <TableColumn>DNI/C.I</TableColumn>
            <TableColumn>Teléfono</TableColumn>
            <TableColumn>País</TableColumn>
            <TableColumn>Materias que dicta</TableColumn>
            <TableColumn>Estatus</TableColumn>
            <TableColumn align="end" />
          </TableHeader>
          <TableBody items={data} emptyContent="No hay resultados">
            {(user) => {
              const phone = `${user?.phonecode?.name || ''}${user.phone || ''}`;
              const dni = `${user?.document_type?.name || ''}${user.document || ''}`;
              const statusColor = getStatusColor(user.status);

              return (
                <TableRow key={user.id}>
                  <TableCell>{moment(user.created_at).format('DD/MM/YYYY')}</TableCell>
                  <TableCell>
                    <User
                      key={user?.photo}
                      avatarProps={{ showFallback: true, radius: 'full', src: user?.photo ? fromPhotos(user.photo) : null }}
                      description={user.email}
                      name={user?.fullname}
                      classNames={{ description: 'text-foreground-500' }}
                    />
                  </TableCell>
                  <TableCell>{dni}</TableCell>
                  <TableCell>{phone}</TableCell>
                  <TableCell>{user.country?.name}</TableCell>
                  <TableCell>
                    {!!user?.subjects?.length && (
                      <span
                        className="cursor-pointer font-semibold text-sm text-secondary"
                        onClick={() => onSelectItem(user, MODAL_ACTION.VIEW)}
                      >
                        Ver
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={classNames('font-medium', statusColor)}>
                      {user.status_text}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <div className="relative flex justify-end items-center gap-2">
                      <Tooltip content="Editar">
                        <IconEdit onClick={() => onSelectItem(user, MODAL_ACTION.EDIT)} />
                      </Tooltip>
                      <Tooltip content="Eliminar" color="danger">
                        <IconTrash className="text-danger" onClick={() => onSelectItem(user, MODAL_ACTION.DELETE)} />
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              )
            }}
          </TableBody>
        </Table>
      ) : (
        <Table
          aria-label="Padres o Representantes Registrados"
          color="primary"
          topContentPlacement="inside"
          topContent={
            <div className="flex flex-row justify-between items-center gap-4">
              <h3 className="text-xl font-medium">Padres o Representantes Registrados</h3>
              <div className='flex gap-2'>
                <Button
                  className="pr-2 bg-blue font-medium text-white"
                  startContent={<IconPlus color="white" />}
                  onPress={() => onSelectItem(null, MODAL_ACTION.CREATE_IN_BULK)}
                >
                  Subir masivamente en excel
                </Button>
                <Button
                  className="pr-2 bg-green font-medium text-white"
                  endContent={<IconPlus color="white" />}
                  onClick={() => onSelectItem(null, MODAL_ACTION.CREATE)}
                >
                  Nuevo
                </Button>
              </div>
            </div>
          }
        >
          <TableHeader>
            <TableColumn style={{ textWrap: 'wrap' }}>Nombre</TableColumn>
            <TableColumn style={{ textWrap: 'wrap' }}>DNI/C.I</TableColumn>
            <TableColumn style={{ textWrap: 'wrap' }}>Teléfono</TableColumn>
            <TableColumn style={{ textWrap: 'wrap' }}>Género</TableColumn>
            <TableColumn style={{ textWrap: 'wrap' }}>País</TableColumn>
            <TableColumn style={{ textWrap: 'wrap' }}>Fecha de Nacimiento</TableColumn>
            <TableColumn style={{ textWrap: 'wrap' }}>Estado Civil</TableColumn>
            <TableColumn style={{ textWrap: 'wrap' }}>Domicilio</TableColumn>
            <TableColumn style={{ textWrap: 'wrap' }}>Representados</TableColumn>
            <TableColumn style={{ textWrap: 'wrap' }}>Estatus</TableColumn>
            <TableColumn align="end" />
          </TableHeader>
          <TableBody items={data} emptyContent="No hay resultados">
            {(user) => {
              const phone = `${user?.phonecode?.name || ''}${user.phone || ''}`;
              const dni = `${user?.document_type?.name || ''}${user.document || ''}`;
              const statusColor = getStatusColor(user.status);

              return (
                <TableRow key={user.id}>
                  <TableCell>
                    <User
                      key={user?.photo}
                      avatarProps={{ showFallback: true, radius: 'full', src: user?.photo ? fromPhotos(user.photo) : null }}
                      description={user?.email}
                      name={user?.fullname}
                      classNames={{ description: 'text-foreground-500' }}
                    />
                  </TableCell>
                  <TableCell>{dni}</TableCell>
                  <TableCell>{phone}</TableCell>
                  <TableCell>{user?.gender?.name}</TableCell>
                  <TableCell>{user?.country?.name}</TableCell>
                  <TableCell>{!!user?.birthdate && moment(user?.birthdate).format('DD/MM/YYYY')}</TableCell>
                  <TableCell>{user?.civil_status?.name}</TableCell>
                  <TableCell>
                    {!!user?.address && (
                      <Popover placement="top" color="default" radius="sm">
                        <PopoverTrigger>
                          <IconMapPin className="text-secondary cursor-pointer" />
                        </PopoverTrigger>
                        <PopoverContent>
                          <div className="p-1 text-small">{user?.address}</div>
                        </PopoverContent>
                      </Popover>
                    )}
                  </TableCell>
                  <TableCell>
                    {user?.represented?.map(item => (
                      <p key={item.id} className="font-semibold text-sm text-secondary mb-0">
                        {item?.student?.fullname}
                      </p>
                    ))}
                  </TableCell>
                  <TableCell>
                    <span className={classNames('font-medium', statusColor)}>
                      {user.status_text}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <div className="relative flex justify-end items-center gap-2">
                      <Tooltip content="Editar">
                        <IconEdit onClick={() => onSelectItem(user, MODAL_ACTION.EDIT)} />
                      </Tooltip>
                      <Tooltip content="Eliminar" color="danger">
                        <IconTrash className="text-danger" onClick={() => onSelectItem(user, MODAL_ACTION.DELETE)} />
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              )
            }}
          </TableBody>
        </Table>
      )}

      <div className="flex w-full justify-center mt-4">
        <Pagination
          showControls
          variant="bordered"
          page={pagination.page}
          total={pagination.pages}
          onChange={goToPage}
        />
      </div>
    </div>
  );
}

const Filters = ({ canResetFilter, filterBy, resetFilter }) => {
  const initialFilter = {
    search: '',
    since: '',
    until: '',
    level_id: Constants.USER.LEVELS.PROFESSOR,
  };
  const [form, setForm] = useState(initialFilter);

  const onChange = (value, target) => {
    setForm(s => ({ ...s, [target]: value }));
    filterBy(value, target);
  }

  return (
    <section className="mb-4 grid sm:flex grid-cols-2 lg:flex-row items-end gap-4">
      <Input
        classNames={{
          base: 'w-full sm:max-w-[15rem]',
          inputWrapper: 'border-1 h-10',
        }}
        label="Buscar"
        labelPlacement="outside"
        placeholder="Nombre del usuario"
        startContent={<IconSearch />}
        variant="bordered"
        radius="sm"
        value={form.search}
        onValueChange={v => onChange(v, 'search')}
      />
      <Input
        type="date"
        classNames={{
          base: 'w-full sm:max-w-[10rem]',
          inputWrapper: 'border-1 h-10',
          input: `pr-0 text-${!!form.since ? '[]' : 'foreground-400'}`,
        }}
        label="Desde"
        labelPlacement="outside"
        placeholder=" "
        variant="bordered"
        radius="sm"
        value={form.since}
        onValueChange={v => onChange(v, 'since')}
      />
      <Input
        type="date"
        classNames={{
          base: 'w-full md:max-w-[10rem]',
          inputWrapper: 'border-1 h-10',
          input: `pr-0 text-${!!form.until ? '[]' : 'foreground-400'}`,
        }}
        label="Hasta"
        labelPlacement="outside"
        placeholder=" "
        variant="bordered"
        radius="sm"
        value={form.until}
        onValueChange={v => onChange(v, 'until')}
      />

      <Button
        className={classNames(
          'max-w-[10rem]',
          form.level_id === Constants.USER.LEVELS.PROFESSOR
            ? 'bg-green border-green text-white'
            : 'bg-white',
        )}
        variant="bordered"
        radius="sm"
        onClick={() => onChange(Constants.USER.LEVELS.PROFESSOR, 'level_id')}
        startContent={
          <img
            src={IconTeacher}
            className={form.level_id === Constants.USER.LEVELS.PROFESSOR ? 'invert brightness-0' : ''}
            alt=""
          />
        }
      >
        Profesores
      </Button>
      <Button
        className={classNames(
          'max-w-[10rem]',
          form.level_id === Constants.USER.LEVELS.PARENT
            ? 'bg-green border-green text-white'
            : 'bg-white',
        )}
        variant="bordered"
        radius="sm"
        onClick={() => onChange(Constants.USER.LEVELS.PARENT, 'level_id')}
        startContent={
          <img
            src={IconParent}
            className={form.level_id === Constants.USER.LEVELS.PARENT ? 'invert brightness-0' : ''}
            alt=""
          />
        }
      >
        <div className="flex flex-col items-start leading-4">
          <span>Padres o</span>
          <span>Representantes</span>
        </div>
      </Button>

      {canResetFilter && (
        <Button
          variant="light"
          className="text-primaryDark"
          onClick={() => {
            setForm(s => ({ ...initialFilter, level_id: s.level_id }));
            resetFilter();
          }}
        >
          Limpiar filtros
        </Button>
      )}
    </section>
  )
}

const ModalViewSubjects = ({ item }) => {


  return (
    <>
      <ModalHeader className="flex flex-col gap-1">Materias que dicta</ModalHeader>
      <ModalBody className="p-0 pb-4">
        <Table aria-label="materias" removeWrapper isStriped classNames={{ th: '!rounded-none' }}>
          <TableHeader>
            <TableColumn>Nombre</TableColumn>
            <TableColumn>Grado</TableColumn>
          </TableHeader>
          <TableBody items={item.subjects ?? []} emptyContent="No hay materias">
            {(subject) => (
              <TableRow key={subject.id}>
                <TableCell>{subject?.subject?.name}</TableCell>
                <TableCell>{subject?.subject?.school_grade?.name}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ModalBody>
    </>
  )
}

const ModalDelete = ({ user, onClose, onDelete }) => {
  return (
    <>
      <ModalHeader className="flex flex-col gap-1">Confirmación</ModalHeader>
      <ModalBody>
        <p className="text-center">¿Estás seguro de eliminar este usuario?</p>
        <p className="text-center font-medium">{user.fullname}</p>
      </ModalBody>
      <ModalFooter className="justify-evenly">
        <Button variant="light" onPress={onClose}>Cancelar</Button>
        <Button color="danger" onPress={onDelete}>Aceptar</Button>
      </ModalFooter>
    </>
  )
}

const useFetchTable = () => {
  const initialFilters = {
    page: 1,
    perPage: Constants.PER_PAGE,
    search: '',
    since: null,
    until: null,
    level_id: Constants.USER.LEVELS.PROFESSOR,
  };

  const initialPagination = {
    page: 1,
    pages: 1,
    total: 0,
    perPage: Constants.PER_PAGE,
  };

  const user = useSelector(state => state.user);
  const [data, setData] = useState([]);
  const [canFetch, setCanFetch] = useState(true);
  const [filters, setFilters] = useState(initialFilters);
  const [pagination, setPagination] = useState(initialPagination);

  const debounceTime = 500;
  const debounce = useRef();

  const fetchData = async () => {
    if (!canFetch) return;
    setCanFetch(false);

    try {
      const response = await UsersService.school.findAll({ ...filters, school_id: user.id });
      const { data, ...rest } = response;

      setData(data);
      setPagination(rest);
      setCanFetch(true);

    } catch (error) {
      setData([]);
      onError(String(error));
    }
  }

  const deleteItem = async (id) => {
    if (!canFetch) return;
    setCanFetch(false);

    const data = {
      id,
      school_id: user.id,
    };

    try {
      await UsersService.school.destroy(data);
      toast.success('Usuario eliminado con éxito');
      fetchData();

    } catch (error) {
      onError(String(error));
    }
    setCanFetch(true);
  }

  const getCurrentPagination = () => {
    // Truco para obtener el estado actualizado (pagination es mantenida con el estado actual por el componente Pagination)
    let pag;
    setPagination(s => {
      pag = s;
      return s;
    });
    return pag;
  }

  const canResetFilter = () => {
    const { page, perPage, level_id, ...initial } = initialFilters;
    const { page: _, perPage: __, level_id: ___, ...current } = filters;
    const initFilter = JSON.stringify(initial);
    const currFilter = JSON.stringify(current);
    return initFilter !== currFilter;
  }

  const onError = (msg) => toast.error(msg);

  const reload = (inSamePage = false) => {
    setCanFetch(true);
    if (!inSamePage) setFilters(s => ({ ...initialFilters, level_id: s.level_id }));
    else fetchData();
  }

  const goToPage = (page) => {
    const pagination = getCurrentPagination();
    if (page >= 1 && page <= pagination.pages && page !== pagination.page) {
      setCanFetch(true);
      setFilters({ ...filters, page });
    }
  }

  const changePerPage = (perPage) => {
    setCanFetch(true);
    setFilters({ ...filters, perPage });
  }

  const filterBy = (value, target) => {
    if (debounce.current) clearTimeout(debounce.current);
    if (target === 'level_id') setData([]);
    debounce.current = setTimeout(() => {
      setCanFetch(true);
      setFilters({ ...filters, page: 1, [target]: value });
    }, debounceTime);
  }

  useEffect(() => {
    fetchData();
  }, [filters]);

  return {
    canResetFilter: canResetFilter(),
    changePerPage,
    data,
    deleteItem,
    filters,
    filterBy,
    goToPage,
    isLoading: !canFetch,
    pagination,
    reload,
  }
}

export default Users;
